import { CheckCircle2, X, XCircle } from "lucide-react";
import { Toast as T } from "radix-ui";
import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

/** `action`: one button on the toast (such as Undo); `duration` in ms (longer when there is an action). */
type ToastAction = { label: string; altText?: string; onClick: () => void };
type ToastItem = { id: number; title: string; description?: string; tone: "success" | "error"; action?: ToastAction; duration?: number };
type ToastFn = (title: string, options?: { description?: string; tone?: "success" | "error"; action?: ToastAction; duration?: number }) => void;

const ToastContext = createContext<ToastFn>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const toast = useCallback<ToastFn>((title, options) => {
    setItems((prev) => [...prev.slice(-2), { id: Date.now() + Math.random(), title, description: options?.description, tone: options?.tone ?? "success", action: options?.action, duration: options?.duration ?? (options?.action ? 8000 : undefined) }]);
  }, []);
  return (
    <ToastContext.Provider value={toast}>
      <T.Provider swipeDirection="right" duration={4000}>
        {children}
        {items.map((item) => (
          <T.Root
            key={item.id}
            type={item.tone === "error" || item.action ? "foreground" : "background"}
            duration={item.duration}
            onOpenChange={(open) => !open && setItems((prev) => prev.filter((t) => t.id !== item.id))}
            className="animate-pop-in flex items-start gap-2.5 rounded-lg border border-line bg-surface-raised p-3 shadow-lg"
          >
            {item.tone === "error" ? (
              <XCircle aria-hidden className="mt-0.5 size-4 shrink-0 text-danger" />
            ) : (
              <CheckCircle2 aria-hidden className="mt-0.5 size-4 shrink-0 text-success" />
            )}
            <div className="min-w-0 flex-1">
              <T.Title className="text-sm font-medium text-fg">{item.title}</T.Title>
              {item.description && <T.Description className="mt-0.5 text-sm text-fg-muted">{item.description}</T.Description>}
            </div>
            {item.action && (
              <T.Action
                altText={item.action.altText ?? item.action.label}
                onClick={item.action.onClick}
                className="-my-0.5 h-7 shrink-0 rounded-md border border-line-strong bg-surface px-2.5 text-sm font-medium text-fg shadow-sm hover:bg-surface-hover"
              >
                {item.action.label}
              </T.Action>
            )}
            <T.Close aria-label="Dismiss" className="-m-1 flex size-6 items-center justify-center rounded-sm text-fg-subtle hover:text-fg">
              <X aria-hidden className="size-3.5" />
            </T.Close>
          </T.Root>
        ))}
        <T.Viewport className="fixed right-4 bottom-4 z-[60] flex w-[min(360px,calc(100vw-2rem))] flex-col gap-2 outline-none" />
      </T.Provider>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
